import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/App";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { trackEvent } from "@/lib/analytics";
import { isFeatureOn } from "@/lib/siteSettings";
import BrandLogo from "@/components/BrandLogo";
import {
  Loader2, CheckCircle2, XCircle, Target, ArrowRight, Sparkles, Crosshair,
} from "lucide-react";

const StepHeader = ({ step, label }) => (
  <div className="flex items-center justify-center gap-2 mb-6" data-testid="welcome-step-header">
    {[1, 2, 3].map((n) => (
      <div key={n} className={`h-1.5 rounded-full transition-all ${n <= step ? "bg-primary w-10" : "bg-secondary w-6"}`} />
    ))}
    <span className="text-xs text-muted-foreground ml-2">Step {step} of 3 - {label}</span>
  </div>
);

const OptionButton = ({ opt, selected, revealed, correctId, onClick, testid }) => {
  let cls = "border-border bg-card hover:border-primary/40";
  if (revealed) {
    if (opt.id === correctId) cls = "border-success bg-success/10";
    else if (selected === opt.id) cls = "border-destructive bg-destructive/10";
    else cls = "border-border bg-card opacity-60";
  } else if (selected === opt.id) {
    cls = "border-primary bg-primary/10";
  }
  return (
    <button
      data-testid={testid}
      disabled={revealed}
      onClick={onClick}
      className={`w-full text-left p-4 rounded-xl border transition-all text-sm ${cls}`}
    >
      <span className="font-semibold uppercase mr-2 text-xs text-muted-foreground">{opt.id}</span>
      {opt.text}
    </button>
  );
};

export default function WelcomePage() {
  const navigate = useNavigate();
  const { user, refresh, isTrialing, isPro } = useAuth();
  const hasTrial = isTrialing || isPro;
  const [stage, setStage] = useState("resolving"); // resolving | intro | check | weakest | drill | results
  const [loading, setLoading] = useState(false);

  // readiness check state
  const [checkQs, setCheckQs] = useState([]);
  const [checkIdx, setCheckIdx] = useState(0);
  const [selected, setSelected] = useState(null);
  const [revealed, setRevealed] = useState(null); // {is_correct, correct_option_id}
  const [scores, setScores] = useState({}); // {subject_id: {correct, total}}
  const [weakest, setWeakest] = useState(null); // {id, name}

  // drill state
  const [drillQs, setDrillQs] = useState([]);
  const [drillIdx, setDrillIdx] = useState(0);
  const [drillResults, setDrillResults] = useState([]); // {q, is_correct, explanation}

  useEffect(() => {
    trackEvent("activation_started");
  }, []);

  // If the user arrived from a pricing CTA (e.g. via Google sign-up), continue
  // straight to that plan's checkout instead of onboarding.
  useEffect(() => {
    let c = null;
    try { c = localStorage.getItem("passadc_pending_checkout"); } catch { /* ignore */ }
    if (c) {
      try { localStorage.removeItem("passadc_pending_checkout"); } catch { /* ignore */ }
      navigate(`/billing?plan=${c}`, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Persist the current onboarding step on the server so a reload/return resumes here.
  const persistStep = (s) => { api.post("/activation/step", { step: s }).catch(() => {}); };

  // Decide where to resume: skip the repeat check when a snapshot was carried in,
  // otherwise resume at the last saved step.
  useEffect(() => {
    if (!user) return;
    let cancelled = false;
    (async () => {
      const act = user.activation || {};
      if (act.completed) { navigate("/dashboard", { replace: true }); return; }
      const carryOn = await isFeatureOn("snapshot_carryover");
      if (cancelled) return;
      const carried = carryOn && !!act.snapshot_source && !!act.weakest_subject;
      if (carried || ((act.step === "weakest" || act.step === "drill" || act.step === "results") && act.weakest_subject)) {
        setWeakest({ id: act.weakest_subject, name: act.weakest_subject_name });
        setStage("weakest");
        return;
      }
      setStage("intro");
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  const skip = async () => {
    trackEvent("activation_skipped", { stage });
    try { await api.post("/activation/skip", {}); } catch { /* noop */ }
    navigate("/dashboard", { replace: true });
  };

  // ---------- Stage 1: readiness check ----------
  const startCheck = async () => {
    setLoading(true);
    try {
      const data = await api.get("/questions/quick-check");
      setCheckQs(data.questions || []);
      setStage("check");
      persistStep("check");
      trackEvent("activation_check_started");
    } catch {
      navigate("/dashboard", { replace: true });
    } finally {
      setLoading(false);
    }
  };

  const submitCheckAnswer = async () => {
    if (!selected || revealed) return;
    const q = checkQs[checkIdx];
    setLoading(true);
    try {
      const res = await api.post("/diagnostic/check", {
        question_id: q.id,
        selected_option_id: selected,
      });
      setRevealed(res);
      const sid = q.subject_id;
      setScores((prev) => ({
        ...prev,
        [sid]: {
          correct: (prev[sid]?.correct || 0) + (res.is_correct ? 1 : 0),
          total: (prev[sid]?.total || 0) + 1,
        },
      }));
    } catch { /* noop */ } finally {
      setLoading(false);
    }
  };

  const nextCheck = async () => {
    if (checkIdx + 1 < checkQs.length) {
      setCheckIdx(checkIdx + 1);
      setSelected(null);
      setRevealed(null);
      return;
    }
    // finished — send scores, get weakest subject
    setLoading(true);
    try {
      const finalScores = { ...scores };
      const res = await api.post("/activation/readiness", { scores: finalScores });
      setWeakest({ id: res.weakest_subject, name: res.weakest_subject_name });
      setStage("weakest");
      persistStep("weakest");
      trackEvent("activation_check_completed", { weakest: res.weakest_subject });
    } catch {
      navigate("/dashboard", { replace: true });
    } finally {
      setLoading(false);
    }
  };

  // ---------- Stage 2: drill ----------
  const startDrill = async () => {
    setLoading(true);
    try {
      const data = await api.get(`/questions?subject_id=${weakest.id}&limit=10`);
      setDrillQs(data.questions || []);
      setSelected(null);
      setRevealed(null);
      setStage("drill");
      persistStep("drill");
      trackEvent("activation_drill_started", { subject: weakest.id });
    } catch { /* noop */ } finally {
      setLoading(false);
    }
  };

  const submitDrillAnswer = async () => {
    if (!selected || revealed) return;
    const q = drillQs[drillIdx];
    setLoading(true);
    try {
      const res = await api.post("/questions/answer", {
        question_id: q.id,
        selected_option_id: selected,
        time_taken_seconds: 20,
        onboarding: true,
      });
      setRevealed(res);
      setDrillResults((prev) => [...prev, { q, is_correct: res.is_correct, explanation: res.explanation }]);
    } catch { /* noop */ } finally {
      setLoading(false);
    }
  };

  const nextDrill = async () => {
    if (drillIdx + 1 < drillQs.length) {
      setDrillIdx(drillIdx + 1);
      setSelected(null);
      setRevealed(null);
      return;
    }
    setStage("results");
    persistStep("results");
    trackEvent("activation_drill_completed", {
      subject: weakest?.id,
      score: drillResults.filter((r) => r.is_correct).length,
      total: drillResults.length,
    });
  };

  const finish = async () => {
    const correct = drillResults.filter((r) => r.is_correct).length;
    try {
      await api.post("/activation/complete", { drill_score: correct, drill_total: drillResults.length });
      await refresh();
    } catch { /* noop */ }
    trackEvent("activation_completed");
    navigate("/dashboard", { replace: true });
  };

  const firstName = (user?.name || "").split(" ")[0];

  return (
    <div className="min-h-screen bg-background flex flex-col" data-testid="welcome-page">
      <header className="flex items-center justify-between px-6 py-4">
        <BrandLogo />
        <button data-testid="welcome-skip-btn" onClick={skip} className="text-xs text-muted-foreground hover:text-foreground transition-colors">
          Skip for now
        </button>
      </header>

      <main className="flex-1 flex items-start justify-center px-4 py-8 md:py-14">
        <div className="w-full max-w-2xl">

          {stage === "resolving" && (
            <div className="flex items-center justify-center py-20" data-testid="welcome-resolving">
              <Loader2 className="w-8 h-8 animate-spin text-primary" />
            </div>
          )}

          {stage === "intro" && (
            <div className="text-center" data-testid="welcome-intro">
              <StepHeader step={1} label="Find where you stand" />
              <div className="w-16 h-16 rounded-2xl bg-primary/15 flex items-center justify-center mx-auto mb-6">
                <Crosshair className="w-8 h-8 text-primary" />
              </div>
              <h1 className="text-3xl md:text-4xl font-bold mb-3">
                Welcome to PassADC{firstName ? `, ${firstName}` : ""}.
              </h1>
              <p className="text-lg text-muted-foreground mb-2">Let's find out where you stand.</p>
              <p className="text-sm text-muted-foreground max-w-md mx-auto mb-8">
                A 3-minute readiness check finds your weakest ADC subject, so your first study session actually moves your score.{hasTrial ? " Your 7-day Pro trial is already active." : " Finish it to unlock 7 days of full Pro, free."}
              </p>
              <Button data-testid="welcome-start-check-btn" size="lg" className="rounded-full px-8 py-6 text-base font-semibold" onClick={startCheck} disabled={loading}>
                {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : <>Find My Weakest ADC Subject <ArrowRight className="ml-2 w-5 h-5" /></>}
              </Button>
              <p className="text-xs text-muted-foreground mt-3">6 questions · about 3 minutes</p>
            </div>
          )}

          {stage === "check" && checkQs[checkIdx] && (
            <div data-testid="welcome-check">
              <StepHeader step={1} label="Find where you stand" />
              <p className="text-xs text-muted-foreground mb-2">Question {checkIdx + 1} of {checkQs.length}</p>
              {checkQs[checkIdx].clinical_vignette && (
                <p className="text-sm text-muted-foreground italic mb-3 p-3 rounded-lg bg-secondary/50 border border-border">{checkQs[checkIdx].clinical_vignette}</p>
              )}
              <h2 className="text-lg font-semibold mb-5">{checkQs[checkIdx].question_text}</h2>
              <div className="space-y-3 mb-6">
                {checkQs[checkIdx].options.map((opt) => (
                  <OptionButton
                    key={opt.id}
                    opt={opt}
                    selected={selected}
                    revealed={!!revealed}
                    correctId={revealed?.correct_option_id}
                    onClick={() => setSelected(opt.id)}
                    testid={`welcome-check-option-${opt.id}`}
                  />
                ))}
              </div>
              {!revealed ? (
                <Button data-testid="welcome-check-submit-btn" className="rounded-full w-full py-6" onClick={submitCheckAnswer} disabled={!selected || loading}>
                  {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : "Check answer"}
                </Button>
              ) : (
                <Button data-testid="welcome-check-next-btn" className="rounded-full w-full py-6" onClick={nextCheck} disabled={loading}>
                  {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : (checkIdx + 1 < checkQs.length ? "Next question" : "See my result")}
                </Button>
              )}
            </div>
          )}

          {stage === "weakest" && weakest && (
            <div className="text-center" data-testid="welcome-weakest">
              <StepHeader step={2} label="Fix your weakest area" />
              <div className="w-16 h-16 rounded-2xl bg-warning/15 flex items-center justify-center mx-auto mb-6">
                <Target className="w-8 h-8 text-warning" />
              </div>
              <p className="text-sm text-muted-foreground mb-1">Based on your readiness check:</p>
              <h1 className="text-2xl md:text-3xl font-bold mb-3">
                Your weakest area appears to be <span className="text-warning">{weakest.name}</span>.
              </h1>
              <p className="text-muted-foreground mb-8">Let's fix that first.</p>
              <Button data-testid="welcome-start-drill-btn" size="lg" className="rounded-full px-8 py-6 text-base font-semibold" onClick={startDrill} disabled={loading}>
                {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : <>Answer 10 {weakest.name} questions <ArrowRight className="ml-2 w-5 h-5" /></>}
              </Button>
            </div>
          )}

          {stage === "drill" && drillQs[drillIdx] && (
            <div data-testid="welcome-drill">
              <StepHeader step={2} label={`Fix your weakest area - ${weakest?.name}`} />
              <p className="text-xs text-muted-foreground mb-2">Question {drillIdx + 1} of {drillQs.length}</p>
              {drillQs[drillIdx].clinical_vignette && (
                <p className="text-sm text-muted-foreground italic mb-3 p-3 rounded-lg bg-secondary/50 border border-border">{drillQs[drillIdx].clinical_vignette}</p>
              )}
              <h2 className="text-lg font-semibold mb-5">{drillQs[drillIdx].question_text}</h2>
              <div className="space-y-3 mb-4">
                {drillQs[drillIdx].options.map((opt) => (
                  <OptionButton
                    key={opt.id}
                    opt={opt}
                    selected={selected}
                    revealed={!!revealed}
                    correctId={revealed?.correct_option_id}
                    onClick={() => setSelected(opt.id)}
                    testid={`welcome-drill-option-${opt.id}`}
                  />
                ))}
              </div>
              {revealed?.explanation && (
                <div className="p-4 rounded-xl bg-secondary border border-border text-sm text-muted-foreground mb-4" data-testid="welcome-drill-explanation">
                  {revealed.explanation}
                </div>
              )}
              {!revealed ? (
                <Button data-testid="welcome-drill-submit-btn" className="rounded-full w-full py-6" onClick={submitDrillAnswer} disabled={!selected || loading}>
                  {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : "Check answer"}
                </Button>
              ) : (
                <Button data-testid="welcome-drill-next-btn" className="rounded-full w-full py-6" onClick={nextDrill}>
                  {drillIdx + 1 < drillQs.length ? "Next question" : "See my results"}
                </Button>
              )}
            </div>
          )}

          {stage === "results" && (
            <div data-testid="welcome-results">
              <StepHeader step={3} label="See what to study next" />
              <div className="text-center mb-8">
                <div className="w-16 h-16 rounded-2xl bg-primary/15 flex items-center justify-center mx-auto mb-5">
                  <Sparkles className="w-8 h-8 text-primary" />
                </div>
                <h1 className="text-2xl md:text-3xl font-bold mb-2">
                  You got {drillResults.filter((r) => r.is_correct).length}/{drillResults.length} in {weakest?.name}.
                </h1>
                <p className="text-muted-foreground text-sm">
                  {drillResults.filter((r) => !r.is_correct).length > 0
                    ? `Here are the ${drillResults.filter((r) => !r.is_correct).length} concepts to review:`
                    : "A perfect run - this subject may be stronger than the snapshot suggested. Your study plan will keep testing it."}
                </p>
              </div>

              {drillResults.filter((r) => !r.is_correct).length > 0 && (
                <div className="space-y-3 mb-8">
                  {drillResults.filter((r) => !r.is_correct).map((r, i) => (
                    <div key={i} className="p-4 rounded-xl bg-card border border-border" data-testid={`welcome-review-concept-${i}`}>
                      <div className="flex items-start gap-2">
                        <XCircle className="w-4 h-4 text-destructive mt-0.5 flex-shrink-0" />
                        <div>
                          <p className="text-sm font-medium mb-1">{r.q.question_text}</p>
                          {r.explanation && <p className="text-xs text-muted-foreground">{r.explanation}</p>}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}

              <div className="p-5 rounded-2xl bg-primary/5 border border-primary/20 mb-6 text-sm">
                <p className="font-semibold mb-1 flex items-center gap-2"><CheckCircle2 className="w-4 h-4 text-success" /> Your focus is set: {weakest?.name}</p>
                <p className="text-muted-foreground">Your dashboard now recommends what to study next{hasTrial ? ", and your 7-day Pro trial keeps everything unlocked while you work through it." : "."}</p>
              </div>

              <Button data-testid="welcome-finish-btn" size="lg" className="rounded-full w-full py-6 text-base font-semibold" onClick={finish}>
                Continue My Study Plan <ArrowRight className="ml-2 w-5 h-5" />
              </Button>
            </div>
          )}
        </div>
      </main>
    </div>
  );
}
